import { useState, useEffect, useCallback, useRef } from "react";
import cloudApi from "../../../services/cloudApi";

const POLL_INTERVAL = 5000; // 5 seconds when task is active

/**
 * Hook to poll task run status for active extractions
 * Refreshes table data on each poll while task is running
 *
 * @param {string|null} taskRunId - The task run ID from table header
 * @param {function} onRefresh - Callback to refresh table data (called on each poll while active)
 * @returns {{ isActive: boolean, status: string|null, startedAt: Date|null }}
 */
export const useTaskRunPolling = (taskRunId, onRefresh) => {
  const [status, setStatus] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [startedAt, setStartedAt] = useState(null);

  const timeoutRef = useRef(null);
  const isVisibleRef = useRef(true);
  const isMountedRef = useRef(true);

  // Check if status indicates an active run
  const isActiveStatus = useCallback((runStatus) => {
    return ['pending', 'running'].includes(runStatus);
  }, []);

  // Fetch task run status
  const fetchStatus = useCallback(async () => {
    if (!taskRunId || !isMountedRef.current) return;

    try {
      const taskRun = await cloudApi.getTaskRun(taskRunId);

      if (!isMountedRef.current) return;

      const newStatus = taskRun?.status;
      const nowActive = isActiveStatus(newStatus);

      setStatus(newStatus);
      setIsActive(nowActive);

      // Parse started_at timestamp
      if (taskRun?.started_at) {
        setStartedAt(new Date(taskRun.started_at));
      } else if (taskRun?.created_at) {
        // Fallback to created_at if started_at not available
        setStartedAt(new Date(taskRun.created_at));
      }

      // Refresh table data while task is running to show new rows
      if (newStatus === 'running' && onRefresh) {
        onRefresh();
      }

      // Schedule next poll if still active and visible
      if (nowActive && isVisibleRef.current) {
        timeoutRef.current = setTimeout(fetchStatus, POLL_INTERVAL);
      }
    } catch (error) {
      console.warn('[useTaskRunPolling] Failed to fetch status:', error.message);

      // Still retry on error if we think it might be active
      if (isVisibleRef.current && isMountedRef.current) {
        timeoutRef.current = setTimeout(fetchStatus, POLL_INTERVAL);
      }
    }
  }, [taskRunId, onRefresh, isActiveStatus]);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === 'visible';

      if (isVisibleRef.current && isActive) {
        // Tab became visible and we have an active task - resume polling
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        fetchStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchStatus, isActive]);

  // Initial fetch and cleanup
  useEffect(() => {
    isMountedRef.current = true;

    if (taskRunId) {
      fetchStatus();
    } else {
      setStatus(null);
      setIsActive(false);
      setStartedAt(null);
    }

    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [taskRunId, fetchStatus]);

  return {
    isActive,
    status,
    startedAt,
  };
};
